import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tropheesService } from '../services/api';

const TropheesPublic = () => {
  const [trophees, setTrophees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState('all_time');

  useEffect(() => {
    loadTrophees();
  }, []);

  const loadTrophees = async () => {
    try {
      const response = await tropheesService.getAll();
      // Le fichier trophees.json contient un objet, pas un tableau
      setTrophees(response.data[0] || response.data);
    } catch (error) {
      console.error('Error loading trophees:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!trophees || !trophees.all_time) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Aucun trophée disponible
        </div>
      </div>
    );
  }

  const getTrophyIcon = (trophyType) => {
    switch (trophyType) {
      case 'roi_de_la_saucisse':
        return 'bi-trophy-fill';
      case 'pilier_de_bar':
        return 'bi-calendar-check-fill';
      case 'compteur_de_points':
        return 'bi-star-fill';
      default:
        return 'bi-award-fill';
    }
  };

  const getTrophyTitle = (trophyType) => {
    switch (trophyType) {
      case 'roi_de_la_saucisse':
        return 'Roi de la Saucisse';
      case 'pilier_de_bar':
        return 'Pilier de Bar';
      case 'compteur_de_points':
        return 'Compteur de Points';
      default:
        return trophyType;
    }
  };

  const getTrophyDescription = (trophyType) => {
    switch (trophyType) {
      case 'roi_de_la_saucisse':
        return 'Personne ayant gagné le plus de saucisses';
      case 'pilier_de_bar':
        return 'Personne ayant le plus de participations';
      case 'compteur_de_points':
        return 'Personne ayant le plus de points';
      default:
        return '';
    }
  };

  const getTrophyColor = (trophyType) => {
    // Toutes les icônes en jaune/warning
    return 'text-warning';
  };

  const getStatLabel = (trophyType) => {
    switch (trophyType) {
      case 'roi_de_la_saucisse':
        return 'Victoires';
      case 'pilier_de_bar':
        return 'Participations';
      case 'compteur_de_points':
        return 'Points';
      default:
        return '';
    }
  };

  const renderTrophyCard = (trophyType, trophyData) => {
    if (!trophyData || !trophyData.players || trophyData.players.length === 0) return null;

    const players = trophyData.players;
    const isTied = players.length > 1;

    return (
      <div key={trophyType} className="col-md-4 mb-4">
        <div className="card h-100 text-center">
          <div className="card-body">
            <div className="mb-3">
              <i
                className={`bi ${getTrophyIcon(trophyType)} ${getTrophyColor(trophyType)}`}
                style={{ fontSize: '4rem' }}
              ></i>
            </div>
            <h4 className="card-title">{getTrophyTitle(trophyType)}</h4>
            <p className="text-muted small mb-3">{getTrophyDescription(trophyType)}</p>

            {/* Afficher la valeur du trophée */}
            <div className="mb-3">
              <div className="text-warning fw-bold" style={{ fontSize: '2rem' }}>
                {trophyData.value}
              </div>
              <div className="text-muted">{getStatLabel(trophyType)}</div>
            </div>

            {/* Afficher le(s) joueur(s) */}
            {isTied && (
              <div className="mb-2">
                <span className="badge bg-warning text-dark">
                  <i className="bi bi-trophy me-1"></i>
                  Égalité
                </span>
              </div>
            )}

            <div className="mt-2">
              {players.map((player, index) => (
                <div key={player.smasheur_id} className={index > 0 ? 'mt-2' : ''}>
                  <Link
                    to={`/smasheurs/${player.smasheur_id}`}
                    className="text-decoration-none"
                  >
                    <h6 className="text-warning mb-0">
                      {player.smasheur_tag && `${player.smasheur_tag} | `}
                      {player.smasheur_name}
                    </h6>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const currentTrophees = selectedSeason === 'all_time'
    ? trophees.all_time
    : trophees.seasons?.find(s => s.season === selectedSeason)?.trophees;

  const currentLabel = selectedSeason === 'all_time'
    ? 'All-Time'
    : trophees.seasons?.find(s => s.season === selectedSeason)?.label;

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="hero-banner mb-4">
        <h1 className="hero-title">
          <i className="bi bi-trophy-fill me-3"></i>
          Trophées Saucismash
        </h1>
        <p className="lead mb-0">
          Les records de la communauté
        </p>
      </div>

      {/* Season Selector */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-3">
              <h5 className="mb-0">
                <i className="bi bi-calendar3 me-2"></i>
                Sélectionner une période
              </h5>
            </div>
            <div className="col-md-9">
              <div className="btn-group flex-wrap" role="group">
                <button
                  type="button"
                  className={`btn ${selectedSeason === 'all_time' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setSelectedSeason('all_time')}
                >
                  <i className="bi bi-infinity me-2"></i>
                  All-Time
                </button>
                {trophees.seasons && trophees.seasons.map((season) => (
                  <button
                    key={season.season}
                    type="button"
                    className={`btn ${selectedSeason === season.season ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setSelectedSeason(season.season)}
                  >
                    {season.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Period Title */}
      <div className="text-center mb-4">
        <h2>
          <i className="bi bi-star-fill text-warning me-2"></i>
          {currentLabel}
        </h2>
      </div>

      {/* Trophies */}
      {currentTrophees ? (
        <div className="row">
          {renderTrophyCard('roi_de_la_saucisse', currentTrophees.roi_de_la_saucisse)}
          {renderTrophyCard('pilier_de_bar', currentTrophees.pilier_de_bar)}
          {renderTrophyCard('compteur_de_points', currentTrophees.compteur_de_points)}
        </div>
      ) : (
        <div className="alert alert-info text-center">
          <i className="bi bi-info-circle me-2"></i>
          Aucune donnée disponible pour cette période
        </div>
      )}

      {/* Legend */}
      <div className="card mt-5">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Légende
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <div className="d-flex align-items-start">
                <i className="bi bi-trophy-fill text-warning me-3" style={{ fontSize: '2rem' }}></i>
                <div>
                  <h6 className="mb-1">Roi de la Saucisse</h6>
                  <p className="small mb-0">
                    Récompense le joueur ayant remporté le plus grand nombre de tournois.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="d-flex align-items-start">
                <i className="bi bi-calendar-check-fill text-warning me-3" style={{ fontSize: '2rem' }}></i>
                <div>
                  <h6 className="mb-1">Pilier de Bar</h6>
                  <p className="small mb-0">
                    Récompense le joueur le plus assidu, celui qui ne rate jamais un tournoi.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="d-flex align-items-start">
                <i className="bi bi-star-fill text-warning me-3" style={{ fontSize: '2rem' }}></i>
                <div>
                  <h6 className="mb-1">Compteur de Points</h6>
                  <p className="small mb-0">
                    Récompense le joueur ayant accumulé le plus de points. (1 point par participation + 1 point par tour passé)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TropheesPublic;
